import type {DiffJson} from '../../../components/diff_json.ts';
import {FILE_DIFF_STATUS, type FileDiffStatus} from '../../types/file_tree.ts';

export function isProjectPageStructuredDiffLoadable(status: FileDiffStatus): boolean {
  return status === FILE_DIFF_STATUS.MODIFIED || status === FILE_DIFF_STATUS.RENAMED;
}

/**
 * Trim a normalized multi-page schematic head JSON down to a single page.
 *
 * The diff tree walker materializes a TreeNode for every leaf reachable from
 * head JSON (including unchanged content), so leaving non-active pages in the
 * payload causes O(file) work per page click. Top-level fields outside `pages`
 * are preserved because metadataCtx and the inspector context read them.
 */
export function scopeHeadJsonToPage(
  headJson: Record<string, unknown>,
  pageId?: string,
): Record<string, unknown> {
  if (!pageId) {
    return headJson;
  }

  const pages = headJson.pages;
  if (!pages || typeof pages !== 'object' || Array.isArray(pages)) {
    return headJson;
  }

  const activePage = (pages as Record<string, unknown>)[pageId];
  if (activePage === undefined) {
    return headJson;
  }

  return {...headJson, pages: {[pageId]: activePage}};
}

/**
 * Trim a crysknife diff_list payload to the active page. Drops `/pages/<id>`
 * entries for other pages; keeps top-level field changes (e.g. `/title`) and
 * any non-page namespaces so downstream grouping still sees them.
 */
export function scopeDiffJsonToPage(
  diffJson: DiffJson,
  pageId?: string,
): DiffJson {
  if (!pageId) {
    return diffJson;
  }

  const scoped: DiffJson = {};

  for (const [key, change] of Object.entries(diffJson)) {
    const parts = key.split('/').filter(Boolean);
    if (parts[0] === 'pages' && parts[1] !== pageId) {
      continue;
    }
    scoped[key] = change;
  }

  return scoped;
}
